"use client";
import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { blogPostsData, BlogPost } from '../SharedBlogData';
import { NewsletterForm } from '@/components/NewsletterForm';
import { format } from 'date-fns';

interface Category {
  id: string;
  name: string;
  count: number;
}

// Category display name map for consistency
const categoryDisplayName: { [key: string]: string } = {
  ai: 'AI & Technology',
  analytics: 'Analytics & Insights',
  commerce: 'E-commerce & Marketplaces',
  'customer-experience': 'Customer Experience & Loyalty',
  entrepreneurship: 'Entrepreneurship',
  growth: 'Growth & Case Studies',
  policy: 'Global Policy'
};

export default function BlogContent() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(blogPostsData);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);

  const categories = useMemo(() => {
  // Get unique categories from blog posts
  const uniqueCategories = [...new Set(blogPosts.map(post => post.category))];
  
  // Create category objects with counts
  const categoryObjects: Category[] = [
    { id: 'all', name: 'All Posts', count: blogPosts.length }
  ];
  
  // Add categories sorted alphabetically
  uniqueCategories.sort().forEach(categoryId => {
    const count = blogPosts.filter(post => post.category === categoryId).length;
    if (count > 0) { // Only include categories that have posts
      categoryObjects.push({
        id: categoryId,
        name: categoryDisplayName[categoryId] || categoryId,
        count
      });
    }
  });
  
  return categoryObjects;
}, [blogPosts]);

  useEffect(() => {
    let filtered = [...blogPosts];
    filtered.sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf());
    
    if (activeCategory === 'all') {
      setFilteredPosts(filtered);
    } else {
      setFilteredPosts(filtered.filter(post => post.category === activeCategory));
    }
  }, [activeCategory, blogPosts]);

  const featuredPosts = activeCategory === 'all' ? blogPosts.filter(post => post.isFeatured) : [];
  const recentPosts = activeCategory === 'all'
    ? blogPosts.filter(post => !post.isFeatured).slice(0, 6)
    : filteredPosts;

  return (
    <>
      {/* Hero Section */}
      <section className="gradient-bg text-white">
        <div className="container py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-white mb-6">
                Perxify Blog
              </h1>
              <p className="text-lg mb-8 text-gray-100">
                Insights, trends, and best practices for building exceptional commerce experiences.
              </p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 rounded-lg h-80 flex items-center justify-center">
                <Image
                  src="/images/hero/blog-visualization.jpg"
                  alt="Blog Visualization"
                  width={600}
                  height={320}
                  priority={true}
                  fetchPriority="high"
                  loading="eager"
                  className="rounded-lg"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts Section - Conditionally rendered only when 'all' is selected */}
      {activeCategory === 'all' && featuredPosts.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="mb-4">
                Featured Posts
              </h2>
              <p className="max-w-3xl mx-auto text-lg">
                Our latest insights and thought leadership on commerce, technology, and customer experience.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {featuredPosts.map(post => (
                <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <Link href={post.customPath} className="block">
                    <Image
                      src={post.image}
                      alt={post.title}
                      width={600}
                      height={256}
                      className="w-full h-80 object-cover rounded-t-lg hover:opacity-90 transition-opacity"
                      style={{ aspectRatio: '75/32' }}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </Link>
                  <div className="p-8">
                    <div className="flex items-center mb-4">
                      <span className="bg-[#E6F7F9] text-[#00A4BD] text-sm px-3 py-1 rounded-full">
                        {categoryDisplayName[post.category] || post.category}
                      </span>
                      <span className="text-gray-500 text-sm ml-4">{format(new Date(post.date), 'MMMM d, yyyy')}</span>
                    </div>
                    <h3 className="text-2xl mb-4">
                      <Link href={post.customPath} className="hover:text-[#008CA1]">
                        {post.title}
                      </Link>
                    </h3>
                    <p className="mb-6">{post.excerpt}</p>
                    <div className="flex items-center mb-6">
                      <Link href={`/team/${post.author.id}`} className="flex items-center hover:opacity-80 transition-opacity">
                        <Image
                          src={post.author.image}
                          alt={post.author.name}
                          width={40}
                          height={40}
                          className="w-12 h-12 object-cover rounded-full mr-3"
                        />
                        <div>
                          <p className="font-medium hover:text-[#00A4BD] transition-colors">{post.author.name}</p>
                          <p className="text-sm text-gray-600">{post.author.jobTitle}</p>
                        </div>
                      </Link>
                    </div>
                    <Link href={post.customPath} className="text-[#00A4BD] font-medium hover:text-[#008CA1]">
                      Read Article →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Posts Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="mb-4">
              Recent Posts
            </h2>
            <p className="max-w-3xl mx-auto text-lg">
              Current market insights, technology developments, and strategic guidance for commerce platform decision-makers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {recentPosts.map(post => (
              <div key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <Link href={post.customPath} className="block">
                  <Image
                    src={post.image}
                    alt={post.title}
                    width={400}
                    height={192}
                    className="w-full h-48 object-cover rounded-t-lg hover:opacity-90 transition-opacity"
                    style={{ aspectRatio: '25/12' }}
                    sizes="(max-width: 1024px) 100vw, 33vw"
                  />
                </Link>
                <div className="p-6">
                  <div className="flex items-center mb-3">
                    <span className="bg-[#E6F7F9] text-[#00A4BD] text-xs px-2 py-1 rounded-full">
                      {categoryDisplayName[post.category] || post.category}
                    </span>
                    <span className="text-gray-500 text-xs ml-3">{format(new Date(post.date), 'MMMM d, yyyy')}</span>
                  </div>
                  <h3 className="text-xl mb-3">
                    <Link href={post.customPath} className="hover:text-[#008CA1]">
                      {post.title}
                    </Link>
                  </h3>
                  <p className="mb-4 text-sm">{post.excerpt}</p>
                  <div className="flex items-center mb-4">
                    <Link href={`/team/${post.author.id}`} className="flex items-center hover:opacity-80 transition-opacity">
                      <Image
                        src={post.author.image}
                        alt={post.author.name}
                        width={32}
                        height={32}
                        className="w-8 h-8 object-cover rounded-full mr-2"
                      />
                      <div>
                        <p className="text-sm font-medium hover:text-[#00A4BD] transition-colors">{post.author.name}</p>
                        <p className="text-xs text-gray-600">{post.author.jobTitle}</p>
                      </div>
                    </Link>
                  </div>
                  <Link href={post.customPath} className="text-[#00A4BD] font-medium hover:text-[#008CA1] text-sm">
                    Read Article →
                  </Link>
                </div>
              </div>
            ))}
            {recentPosts.length === 0 && (
              <div className="text-center py-12 col-span-1 md:col-span-2 lg:col-span-3">
                <p className="text-gray-500">No recent posts found in this category.</p>
              </div>
            )}
          </div>
          <div className="text-center mt-12">
            <Link href="/blog/all-posts" className="btn-primary inline-block">
              View All Posts
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="mb-4">
              Browse by Category
            </h2>
            <p className="max-w-3xl mx-auto text-lg">
              Explore our content by topic to find the insights most relevant to you.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`bg-gray-50 hover:bg-[#E6F7F9] p-6 rounded-lg text-center transition-colors ${
                  activeCategory === category.id ? 'bg-[#E6F7F9] text-[#00A4BD]' : ''
                }`}
              >
                <div className="w-12 h-12 bg-[#E6F7F9] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-[#00A4BD]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {category.id === 'commerce' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    )}
                    {category.id === 'marketplaces' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                      />
                    )}
                    {category.id === 'analytics' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    )}
                    {category.id === 'customer-experience' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    )}
                    {category.id === 'ai' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                      />
                    )}
                    {category.id === 'loyalty' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zM12 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zM12 2v2m0 18v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
                      />
                    )}
                    {category.id === 'technology' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                      />
                    )}
                    {category.id === 'case-study' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    )}
                    {category.id === 'entrepreneurship' && (
                      <path 
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    )}
                    {category.id === 'growth' && (
                      <path 
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d='M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z'
                      />
                    )}
                    {category.id === 'policy' && (
                      <path 
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    )}
                    {category.id === 'all' && (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    )}
                  </svg>
                </div>
                <h3 className="font-medium mb-1">{category.name}</h3>
                <p className="text-sm text-gray-600">{category.count} Articles</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section bg-white">
        <div className="container">
          <NewsletterForm />
        </div>
      </section>
    </>
  );
}